import { useState, useCallback } from "react";
import useApi from "@/hooks/useApi";

export interface WorkflowVersionMeta {
  id: number;
  workflowName: string;
  triggerType: string;
  savedBy: string;
  isPublished: boolean;
  isAutosave: boolean;
  savedAt: string;
  nodeCount: number;
}

export interface WorkflowVersionDetail extends WorkflowVersionMeta {
  nodesJson: string;
}

export default function useWorkflowVersions(workflowId: number | string) {
  const api = useApi();
  const [versions, setVersions] = useState<WorkflowVersionMeta[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadVersions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.admin.get(`WorkflowVersions/${workflowId}`);
      const data = await res.json();
      setVersions(Array.isArray(data) ? data : []);
    } catch {
      setError("Failed to load version history.");
      setVersions([]);
    } finally {
      setLoading(false);
    }
  }, [api.admin, workflowId]);

  const getVersion = useCallback(
    async (versionId: number): Promise<WorkflowVersionDetail | null> => {
      try {
        const res = await api.admin.get(
          `WorkflowVersions/${workflowId}/${versionId}`
        );
        if (!res.ok) return null;
        return await res.json();
      } catch {
        return null;
      }
    },
    [api.admin, workflowId]
  );

  const saveVersion = useCallback(
    async (params: {
      workflowId: number;
      workflowName: string;
      triggerType: string;
      nodesJson: string;
      isPublished: boolean;
      isAutosave: boolean;
    }) => {
      try {
        await api.admin.post("WorkflowVersions", params);
      } catch {
        // Non-fatal — version history is best-effort
      }
    },
    [api.admin]
  );

  return { versions, loading, error, loadVersions, getVersion, saveVersion };
}
